import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAppState from '../../AppStore';
import usePrintArea from './api/usePrintArea';
import { ToolNames } from '../ToolNames';

const SVG_URL = 'http://www.w3.org/2000/svg';

const createSvgElement = (type: string, attributes: Record<string, string>) => {
  const element = document.createElementNS(SVG_URL, type);
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
  return element;
};

const coordinatesToPath = (coordinates: [number, number][]): string => {
  const result = coordinates.reduce((path, coord, index) => {
    return `${path} ${index === 0 ? 'M' : 'L'}${coord[0]} ${coord[1]}`;
  }, '');

  return `${result} Z`;
};

const PrintPreview = () => {
  const { t } = useTranslation('agisviewer');

  const activeTool = useAppState((state) => state.activeTool);
  const location = useAppState((state) => state.location);
  const formatValue = useAppState((state) => state.formatValue);
  const orientation = useAppState((state) => state.orientation);
  const printScale = useAppState((state) => state.printScale);
  const setPrintScale = useAppState((state) => state.setPrintScale);
  const setMapExtent = useAppState((state) => state.setMapExtent);
  const setNavigationIsEnabled = useAppState((state) => state.setNavigationIsEnabled);
  const addNotification = useAppState((state) => state.addNotification);
  const removeNotification = useAppState((state) => state.removeNotification);
  const navigationIsEnabled = useAppState((state) => state.navigationIsEnabled);

  const svgRef = useRef<HTMLDivElement>(null);
  const [notificationId, setNotificationId] = useState<string | null>(null);

  const { getMapSizeInPx, getPreviewExtent, getPrintExtent, setFitExtent } =
    usePrintArea();

  useEffect(() => {
    const parentElement = svgRef.current?.parentElement;
    if (!parentElement) return () => {};

    const handleWheel = (event: WheelEvent) => {
      if (!notificationId) {
        const id = Date.now().toString();
        addNotification({
          id,
          title: t('print_navigation_notification_title'),
          message: t('print_navigation_notification'),
          type: 'negative',
        });

        setNotificationId(id);
      }
      event.preventDefault();
    };

    parentElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => parentElement.removeEventListener('wheel', handleWheel);
  }, [activeTool, svgRef, notificationId]);

  useEffect(() => {
    if (activeTool !== ToolNames.print && notificationId) {
      removeNotification(notificationId);
      setNotificationId(null);
    }
  }, [activeTool, removeNotification]);

  useEffect(() => {
    const printExtent = getPrintExtent();
    if (printExtent) {
      setMapExtent(printExtent);
    }
  }, [location, formatValue, orientation, printScale]);

  useEffect(() => {
    setFitExtent();
  }, [printScale, formatValue, orientation]);

  useEffect(() => {
    if (activeTool === ToolNames.print) {
      setNavigationIsEnabled(false);
    } else {
      setNavigationIsEnabled(true);
    }
  }, [activeTool]);

  useEffect(() => {
    if (!navigationIsEnabled && activeTool === ToolNames.print) {
      setFitExtent();
      setPrintScale(printScale);
    }
  }, [navigationIsEnabled, activeTool]);

  useEffect(() => {
    if (!svgRef.current) return;

    const { mapWidthInPx, mapHeightInPx } = getMapSizeInPx();
    const previewExtent = getPreviewExtent();

    if (!previewExtent) return;

    const { xMin, yMin, xMax, yMax } = previewExtent;

    const outerRing: [number, number][] = [
      [0, 0],
      [mapWidthInPx, 0],
      [mapWidthInPx, mapHeightInPx],
      [0, mapHeightInPx],
    ];

    const innerRing: [number, number][] = [
      [xMin, yMax],
      [xMin, yMin],
      [xMax, yMin],
      [xMax, yMax],
      [xMin, yMax],
    ];

    if (!outerRing || !innerRing) return;

    const svgElement = createSvgElement('svg', {
      width: '100%',
      height: '100%',
      style: 'pointer-events: none; position: absolute; top: 0;',
    });

    const d = `${coordinatesToPath(outerRing)} ${coordinatesToPath(innerRing)}`;

    const svgOuterRing = createSvgElement('path', {
      d,
      fill: 'black',
      'fill-opacity': '0.5',
      'fill-rule': 'evenodd',
    });

    svgElement.appendChild(svgOuterRing);

    // reset the content
    svgRef.current.innerHTML = '';
    svgRef.current.appendChild(svgElement);
  }, [activeTool, formatValue, orientation, printScale, getPrintExtent]);

  if (activeTool !== ToolNames.print) return null;

  return <div className="print-preview" ref={svgRef} />;
};

export default PrintPreview;
